// MSMEAgriSoloSeedMatcher.jsx
// Responsive solo farm-themed match-3 game.

import React, { useState, useEffect, useRef } from 'react';

// CONFIG
const ROWS = 8;
const COLS = 8;
const TILE_TYPES = ['🌽','🌾','🍅','🥕','🍉'];
const START_MOVES = 30;
const SCORE_TO_NEXT_LEVEL = 1500;

function randTile() {
  return TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)];
}

function createBoard() {
  const b = Array(ROWS).fill(null).map(() => Array(COLS).fill(null));
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      b[r][c] = randTile();
    }
  }
  return b;
}

export default function MSMEAgriSoloSeedMatcher() {
  const [board, setBoard] = useState(() => createBoard());
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(START_MOVES);
  const [level, setLevel] = useState(1);
  const [message, setMessage] = useState('Welcome Farmer! Match 3 or more to harvest.');
  const animating = useRef(false);
  const [showHelp, setShowHelp] = useState(false);

  function cloneBoard(b) {
    return b.map(row => row.slice());
  }
  function isAdjacent(a, b) {
    if (!a || !b) return false;
    const dr = Math.abs(a.r - b.r);
    const dc = Math.abs(a.c - b.c);
    return (dr === 1 && dc === 0) || (dr === 0 && dc === 1);
  }
  function swapTiles(b, a, bpos) {
    const nb = cloneBoard(b);
    const tmp = nb[a.r][a.c];
    nb[a.r][a.c] = nb[bpos.r][bpos.c];
    nb[bpos.r][bpos.c] = tmp;
    return nb;
  }
  function findMatches(b) {
    const matches = new Set();
    for (let r = 0; r < ROWS; r++) {
      let runChar = null;
      let runStart = 0;
      for (let c = 0; c <= COLS; c++) {
        const ch = c < COLS ? b[r][c] : null;
        if (ch === runChar) continue;
        const runLen = c - runStart;
        if (runChar !== null && runLen >= 3) {
          for (let k = runStart; k < c; k++) matches.add(`${r},${k}`);
        }
        runChar = ch;
        runStart = c;
      }
    }
    for (let c = 0; c < COLS; c++) {
      let runChar = null;
      let runStart = 0;
      for (let r = 0; r <= ROWS; r++) {
        const ch = r < ROWS ? b[r][c] : null;
        if (ch === runChar) continue;
        const runLen = r - runStart;
        if (runChar !== null && runLen >= 3) {
          for (let k = runStart; k < r; k++) matches.add(`${k},${c}`);
        }
        runChar = ch;
        runStart = r;
      }
    }
    return Array.from(matches).map(str => {
      const [r,c] = str.split(',').map(Number);
      return {r,c};
    });
  }
  function collapseAndRefill(b, matched) {
    const nb = cloneBoard(b);
    matched.forEach(({r,c}) => nb[r][c] = null);
    for (let c = 0; c < COLS; c++) {
      let write = ROWS - 1;
      for (let r = ROWS -1; r >= 0; r--) {
        if (nb[r][c] !== null) {
          nb[write][c] = nb[r][c];
          write--;
        }
      }
      for (let r = write; r >= 0; r--) nb[r][c] = randTile();
    }
    return nb;
  }
  function scoreForMatch(n) { return n * 50 * level; }

  async function handleSwap(r,c) {
    if (animating.current) return;
    const pos = {r,c};
    if (!selected) { setSelected(pos); return; }
    if (selected.r === r && selected.c === c) { setSelected(null); return; }
    if (!isAdjacent(selected, pos)) { setSelected(pos); return; }

    const swapped = swapTiles(board, selected, pos);
    const matches = findMatches(swapped);
    if (matches.length === 0) {
      setMessage('No harvest — no match! Try another swap.');
      setBoard(swapped);
      setSelected(null);
      animating.current = true;
      await new Promise(res => setTimeout(res, 300));
      setBoard(board);
      animating.current = false;
      return;
    }
    setSelected(null);
    setMoves(m => Math.max(0, m-1));
    animating.current = true;
    let working = swapped;
    let totalRemoved = 0;
    while (true) {
      const ms = findMatches(working);
      if (ms.length === 0) break;
      totalRemoved += ms.length;
      setScore(s => s + scoreForMatch(ms.length));
      setBoard(working);
      await new Promise(res => setTimeout(res, 300));
      working = collapseAndRefill(working, ms);
      setBoard(working);
      await new Promise(res => setTimeout(res, 300));
    }
    animating.current = false;
    setMessage(`Nice! Harvested ${totalRemoved} tiles.`);
  }

  useEffect(() => {
    let b = cloneBoard(board);
    let changed = false;
    while (true) {
      const ms = findMatches(b);
      if (ms.length === 0) break;
      changed = true;
      b = collapseAndRefill(b, ms);
    }
    if (changed) setBoard(b);
  }, []);

  useEffect(() => {
    if (score >= level * SCORE_TO_NEXT_LEVEL) {
      setLevel(l => l+1);
      setMoves(m => m + 10);
      setMessage(`Level up! Welcome to level ${level+1}. Extra moves awarded.`);
    }
  }, [score]);

  useEffect(() => {
    if (moves <= 0) {
      setMessage('No more moves. Game over — try again or shuffle!');
    }
  }, [moves]);

  function shuffleBoard() {
    if (animating.current) return;
    const b = createBoard();
    setBoard(b);
    setMessage('Shuffled the field — new layout.');
    setMoves(m => Math.max(0, m-3));
  }
  function hint() {
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const dirs = [{r:1,c:0},{r:-1,c:0},{r:0,c:1},{r:0,c:-1}];
        for (const d of dirs) {
          const nr = r + d.r, nc = c + d.c;
          if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
          const sb = swapTiles(board, {r,c}, {r:nr,c:nc});
          const ms = findMatches(sb);
          if (ms.length > 0) {
            setMessage(`Hint: swap (${r+1},${c+1}) with (${nr+1},${nc+1})`);
            return;
          }
        }
      }
    }
    setMessage('No hints found — try shuffle.');
  }
  function resetGame() {
    setBoard(createBoard());
    setScore(0);
    setMoves(START_MOVES);
    setLevel(1);
    setSelected(null);
    setMessage('New season — good luck!');
  }

  return (
    <div style={{fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial', padding:20, maxWidth:980, margin:'0 auto'}}>
      <style>{`
        .board{display:grid; grid-gap:6px; margin:12px auto; background:#7fb77e; padding:10px; border-radius:12px; box-shadow:0 6px 18px rgba(0,0,0,0.15)}
        .tile{aspect-ratio:1; width:100%; max-width:54px; background:#fff; display:flex; align-items:center; justify-content:center; font-size:clamp(18px,4vw,28px); border-radius:8px; box-shadow:0 3px 6px rgba(0,0,0,0.12); cursor:pointer; user-select:none}
        .tile.selected{outline:3px solid #ffd54f; transform:scale(1.05)}
        .info{display:flex; flex-wrap:wrap; gap:10px; align-items:center; justify-content:space-between}
        .panel{background:#fff; padding:8px 12px; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.06)}
        .btn{padding:8px 10px; border-radius:8px; cursor:pointer; border:none}
        .btn-primary{background:#2e7d32; color:#fff}
        .btn-ghost{background:transparent; border:1px solid #ddd}
        .meta{display:flex; gap:8px; align-items:center; flex-wrap:wrap}
        @media(max-width:768px){
          .layout{flex-direction:column}
          .sidebar{width:100%!important}
        }
      `}</style>

      <h2 style={{margin:0}}>🌱 MSMEAgri Solo — Seed Matcher</h2>
      <p style={{marginTop:8, marginBottom:12}}>{message}</p>

      <div className="info">
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <div className="panel">Score: <strong>{score}</strong></div>
          <div className="panel">Moves: <strong>{moves}</strong></div>
          <div className="panel">Level: <strong>{level}</strong></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full max-w-md">
            <button className="btn btn-primary w-full" onClick={resetGame}>
                New Season
            </button>
            <button className="btn btn-ghost w-full" onClick={shuffleBoard}>
                Shuffle (-3)
            </button>
            <button className="btn btn-ghost w-full" onClick={hint}>
                Hint
            </button>
            <button
                onClick={() => setShowHelp(!showHelp)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg w-full"
            >
                {showHelp ? "Close Help" : "Help"}
            </button>
        </div>
      </div>

      <div className="layout" style={{display:'flex', gap:12, marginTop:12}}>
        <div style={{flex:1, minWidth:0}}>
          <div className="board" style={{gridTemplateColumns:`repeat(${COLS},minmax(0,1fr))`}}>
            {board.flatMap((row, r) => row.map((tile, c) => {
              const isSel = selected && selected.r===r && selected.c===c;
              return (
                <div key={`${r}-${c}`} className={`tile ${isSel? 'selected': ''}`} onClick={() => handleSwap(r,c)}>
                  <span aria-hidden>{tile}</span>
                </div>
              );
            }))}
          </div>
        </div>

        {showHelp && (
        <div className="sidebar" style={{ width: 260, flexShrink: 0 }}>
            <div className="panel text-black" style={{ marginBottom: 8 }}>
            <h4 style={{ margin: '0 0 8px 0' }}>How to Play</h4>
            <ol style={{ paddingLeft: 18, margin: 0 }}>
                <li>Tap a tile, then tap an adjacent tile to swap.</li>
                <li>Make rows or columns of 3+ to harvest.</li>
                <li>Use moves wisely — reach the score goal to level up.</li>
            </ol>
            </div>

            <div className="panel text-black" style={{ marginBottom: 8 }}>
            <h4 style={{ margin: '0 0 8px 0' }}>Power-ups & Tips</h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Shuffle costs 3 moves but can rescue you.</li>
                <li>Combos score higher.</li>
                <li>Level up grants extra moves.</li>
            </ul>
            </div>

            <div className="panel text-black">
            <h4 style={{ margin: '0 0 8px 0' }}>Session Summary</h4>
            <div className="meta">
                <strong>Next level target:</strong>
                <span>{level * SCORE_TO_NEXT_LEVEL}</span>
            </div>
            <div className="meta">
                <strong>Tile types:</strong>
                <span>{TILE_TYPES.join(' ')}</span>
            </div>
            </div>
        </div>
        )}
      </div>

      <footer style={{marginTop:12, fontSize:13, color:'#555'}}>
        Made for farmers — solo gameplay.
      </footer>
    </div>
  );
}
